// =============================================================================
// features/sales/campaigns/operate/components/OperateHeader.tsx — Cabeçalho
// da operação: campanha, situação da telefonia (placeholder inerte) e o
// countdown do lease (doc 26 §6.8).
//
// "Posição na fila": a API (`POST /queue/claim-next`) não devolve posição
// nem total — não fabricamos um número (mesma convenção honesta de
// hooks/sales/useCampaigns.ts, ver CampaignsList.tsx "Indisponível").
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { CampaignStatusBadge } from '../../components/CampaignStatusBadge';

import { QueueLeaseIndicator } from './QueueLeaseIndicator';

export interface OperateHeaderProps {
  campaign: Campaign;
  claimExpiresAt: string | null;
  leaseLost: boolean;
}

export function OperateHeader({
  campaign,
  claimExpiresAt,
  leaseLost,
}: OperateHeaderProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <Link
        to="/campanhas"
        className="inline-flex items-center gap-1.5 self-start font-sans text-xs font-semibold text-ink-3 hover:text-azul transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40 rounded-sm"
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          className="w-3.5 h-3.5"
          aria-hidden="true"
        >
          <path d="M10 3.5 5.5 8l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Campanhas
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-2xl)',
                letterSpacing: '-0.04em',
                lineHeight: 1.1,
                fontVariationSettings: "'opsz' 32",
              }}
            >
              {campaign.name}
            </h1>
            <CampaignStatusBadge status={campaign.status} />
          </div>
          <p className="font-sans text-xs text-ink-4">
            A posição exata na fila não é exibida — cada &quot;Próximo cliente&quot; busca o item
            mais prioritário disponível no momento.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 rounded-pill px-3 py-1.5 font-sans text-xs font-semibold text-ink-4"
            style={{ background: 'var(--surface-muted)', boxShadow: 'var(--elev-1)' }}
            title="Discagem automática chega em F36 — hoje a ligação é feita manualmente."
          >
            Telefonia manual
          </span>
          <QueueLeaseIndicator claimExpiresAt={claimExpiresAt} leaseLost={leaseLost} />
        </div>
      </div>
    </div>
  );
}
